import { Capacitor, registerPlugin } from '@capacitor/core';

// Bridge to the Capacitor NativeProtection plugin (apps/mobile-shell).
// In a plain browser every call is a safe no-op; inside the native shell the
// plugin enforces FLAG_SECURE (Android) and screen-capture detection (iOS).

type NativeProtectionPlugin = {
  setCaptureProtection(options: { enabled: boolean }): Promise<void>;
  enterPip?(): Promise<void>;
  setBackgroundKeepAlive?(options: { enabled: boolean }): Promise<void>;
  addListener(eventName: 'captureDetected', listener: () => void): Promise<{ remove: () => void }>;
  addListener(eventName: 'appStateChanged', listener: (state: { isActive: boolean }) => void): Promise<{ remove: () => void }>;
};

const NativeProtection = registerPlugin<NativeProtectionPlugin>('NativeProtection');

export function runningNatively() {
  return Capacitor.isNativePlatform();
}

async function plugin(): Promise<NativeProtectionPlugin | undefined> {
  if (!Capacitor.isNativePlatform()) return undefined;
  return NativeProtection;
}

/**
 * Enable while video playback is active: blocks screenshots/recording on Android
 * (FLAG_SECURE) and arms recording detection on iOS. Hardcoded product decision —
 * intentionally not gated by any BuildConfig flag.
 */
export async function setCaptureProtection(enabled: boolean) {
  const native = await plugin();
  if (!native) return;
  try { await native.setCaptureProtection({ enabled }); } catch { /* never break playback */ }
}

/** Fires when the OS reports an active screen capture session (iOS). */
export async function onCaptureDetected(callback: () => void) {
  const native = await plugin();
  if (!native) return () => undefined;
  const handle = await native.addListener('captureDetected', callback).catch(() => undefined);
  return () => void handle?.remove();
}

export async function onAppStateChanged(callback: (isActive: boolean) => void) {
  const native = await plugin();
  if (!native) return () => undefined;
  const handle = await native.addListener('appStateChanged', (state) => callback(state.isActive)).catch(() => undefined);
  return () => void handle?.remove();
}

/** Optional Android picture-in-picture continuation. */
export async function enterPictureInPicture() {
  const native = await plugin();
  try { await native?.enterPip?.(); } catch { /* unsupported */ }
}

/** Keep the media process alive in background (Android foreground service). */
export async function setBackgroundKeepAlive(enabled: boolean) {
  const native = await plugin();
  try { await native?.setBackgroundKeepAlive?.({ enabled }); } catch { /* unsupported */ }
}
